/**
 * TKBC (Thai Kickboxing Club) weekly schedule configuration.
 *
 * Each entry maps a weekday + time slot to a session type so the app can
 * auto-suggest the current class when a trainer taps "Jetzt-Session starten".
 *
 * Times are in 24-hour format (HH:MM). The matcher picks the slot whose
 * start time is closest to "now" within a configurable tolerance window.
 */

export interface ScheduleSlot {
  day: number          // 0 = Sunday … 6 = Saturday  (Date#getDay())
  startTime: string    // "HH:MM"
  endTime: string      // "HH:MM"
  title: { de: string; en: string }
  type: 'muay_thai' | 'boxing' | 'kids' | 'fitness' | 'sparring' | 'open_gym' | 'special'
  durationMinutes: number
}

export const TKBC_SCHEDULE: ScheduleSlot[] = [
  // Monday
  { day: 1, startTime: '17:30', endTime: '18:30', title: { de: 'Kids Muay Thai', en: 'Kids Muay Thai' }, type: 'kids', durationMinutes: 60 },
  { day: 1, startTime: '18:30', endTime: '19:30', title: { de: 'Muay Thai Anfaenger', en: 'Muay Thai Beginners' }, type: 'muay_thai', durationMinutes: 60 },
  { day: 1, startTime: '19:30', endTime: '20:30', title: { de: 'Muay Thai Fortgeschrittene', en: 'Muay Thai Advanced' }, type: 'muay_thai', durationMinutes: 60 },

  // Tuesday
  { day: 2, startTime: '18:00', endTime: '19:00', title: { de: 'Boxen', en: 'Boxing' }, type: 'boxing', durationMinutes: 60 },
  { day: 2, startTime: '19:00', endTime: '20:00', title: { de: 'Muay Thai All Levels', en: 'Muay Thai All Levels' }, type: 'muay_thai', durationMinutes: 60 },
  { day: 2, startTime: '20:00', endTime: '21:00', title: { de: 'Sparring', en: 'Sparring' }, type: 'sparring', durationMinutes: 60 },

  // Wednesday
  { day: 3, startTime: '17:30', endTime: '18:30', title: { de: 'Kids Muay Thai', en: 'Kids Muay Thai' }, type: 'kids', durationMinutes: 60 },
  { day: 3, startTime: '18:30', endTime: '19:30', title: { de: 'Muay Thai Anfaenger', en: 'Muay Thai Beginners' }, type: 'muay_thai', durationMinutes: 60 },
  { day: 3, startTime: '19:30', endTime: '20:30', title: { de: 'Muay Thai Fortgeschrittene', en: 'Muay Thai Advanced' }, type: 'muay_thai', durationMinutes: 60 },

  // Thursday
  { day: 4, startTime: '18:00', endTime: '19:00', title: { de: 'Boxen', en: 'Boxing' }, type: 'boxing', durationMinutes: 60 },
  { day: 4, startTime: '19:00', endTime: '20:00', title: { de: 'Muay Thai All Levels', en: 'Muay Thai All Levels' }, type: 'muay_thai', durationMinutes: 60 },
  { day: 4, startTime: '20:00', endTime: '21:00', title: { de: 'Fitness & Conditioning', en: 'Fitness & Conditioning' }, type: 'fitness', durationMinutes: 60 },

  // Friday
  { day: 5, startTime: '17:30', endTime: '18:30', title: { de: 'Kids Muay Thai', en: 'Kids Muay Thai' }, type: 'kids', durationMinutes: 60 },
  { day: 5, startTime: '18:30', endTime: '19:30', title: { de: 'Muay Thai Open Gym', en: 'Muay Thai Open Gym' }, type: 'open_gym', durationMinutes: 60 },

  // Saturday
  { day: 6, startTime: '10:00', endTime: '11:30', title: { de: 'Muay Thai Weekend Session', en: 'Muay Thai Weekend Session' }, type: 'muay_thai', durationMinutes: 90 },
  { day: 6, startTime: '11:30', endTime: '12:30', title: { de: 'Sparring / Open Mat', en: 'Sparring / Open Mat' }, type: 'sparring', durationMinutes: 60 },
]

const TOLERANCE_MINUTES = 45

function toMinutesSinceMidnight(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

/**
 * Find the schedule slot that best matches the given date/time.
 *
 * Returns `null` when nothing is within the tolerance window.
 */
export function matchCurrentScheduleSlot(now: Date = new Date()): ScheduleSlot | null {
  const dayOfWeek = now.getDay()
  const nowMinutes = now.getHours() * 60 + now.getMinutes()

  const todaySlots = TKBC_SCHEDULE.filter((s) => s.day === dayOfWeek)
  if (todaySlots.length === 0) return null

  let best: ScheduleSlot | null = null
  let bestDelta = Infinity

  for (const slot of todaySlots) {
    const startMin = toMinutesSinceMidnight(slot.startTime)
    const endMin = toMinutesSinceMidnight(slot.endTime)

    // during the class or within tolerance before/after
    const delta =
      nowMinutes >= startMin && nowMinutes <= endMin
        ? 0
        : Math.min(Math.abs(nowMinutes - startMin), Math.abs(nowMinutes - endMin))

    if (delta < bestDelta) {
      bestDelta = delta
      best = slot
    }
  }

  return best && bestDelta <= TOLERANCE_MINUTES ? best : null
}

/**
 * Get all schedule slots for a given weekday.
 */
export function getSlotsForDay(dayOfWeek: number): ScheduleSlot[] {
  return TKBC_SCHEDULE.filter((s) => s.day === dayOfWeek)
}

/**
 * Generate a session title from a schedule slot.
 */
export function sessionTitleFromSlot(slot: ScheduleSlot, lang: 'de' | 'en'): string {
  return `${slot.title[lang]} (${slot.startTime}–${slot.endTime})`
}

export const WEEKDAY_LABELS: Record<number, { de: string; en: string }> = {
  0: { de: 'Sonntag', en: 'Sunday' },
  1: { de: 'Montag', en: 'Monday' },
  2: { de: 'Dienstag', en: 'Tuesday' },
  3: { de: 'Mittwoch', en: 'Wednesday' },
  4: { de: 'Donnerstag', en: 'Thursday' },
  5: { de: 'Freitag', en: 'Friday' },
  6: { de: 'Samstag', en: 'Saturday' },
}
